import dayjs from "dayjs"
import { type ReactNode } from "react"

import TryoutResultTag from "#components/TryoutResultTag"
import { type TryoutForTable } from "#customTypes/composedTypes/tryoutTypes"

import type { ColumnsType } from "antd/es/table"

/**
 * Column config for the Tryouts table (rendered via Table2).
 *
 * Sorting is controlled by the data hook: it sorts the full dataset before
 * truncation and persists the choice in userSettings. Table2 injects the
 * active `sortOrder` onto the matching column key, so these `sorter`
 * comparators only need to match the hook's ordering for consistent display.
 *
 * `startDate` sorts on the hidden numeric `eventStart` (epoch ms), which
 * encodes both date and time, so descending gives date-desc then time-desc.
 * `result` sorts on the hidden numeric `resultRank` (Pending → Passed →
 * Failed) rather than alphabetically on the label.
 */
export const TRYOUT_SORTABLE_FIELDS = [
  "studentName",
  "result",
  "className",
  "startDate",
  "lastUpdatedAt",
] as const

export const TRYOUT_TABLE_COLUMN_CONFIG: ColumnsType<TryoutForTable> = [
  {
    title: "DOB",
    dataIndex: "studentDob",
    key: "studentDob",
    width: 64,
    align: "center",
  },
  {
    title: "Student Name",
    dataIndex: "studentName",
    key: "studentName",
    width: 250,
    ellipsis: true,
    sorter: (a, b) => a.studentName.localeCompare(b.studentName),
  },
  {
    title: "Progress",
    dataIndex: "progress",
    key: "progress",
    width: 110,
    render: (value: ReactNode) => value,
  },
  {
    title: "Result",
    dataIndex: "resultRank",
    key: "result",
    width: 100,
    align: "center",
    render: (_value: number, row) => <TryoutResultTag result={row.result} />,
    sorter: (a, b) => a.resultRank - b.resultRank,
  },
  {
    title: "Class Name",
    dataIndex: "className",
    key: "className",
    width: 250,
    ellipsis: true,
    sorter: (a, b) => a.className.localeCompare(b.className),
  },
  {
    title: "Start Date",
    dataIndex: "startDate",
    key: "startDate",
    width: 110,
    sorter: (a, b) => a.eventStart - b.eventStart,
  },
  {
    title: "Start Time",
    dataIndex: "startTime",
    key: "startTime",
    width: 90,
    align: "right",
  },
  {
    title: "End Time",
    dataIndex: "endTime",
    key: "endTime",
    width: 90,
    align: "right",
  },
  {
    title: "Semester",
    dataIndex: "semester",
    key: "semester",
    width: 110,
    ellipsis: true,
  },
  {
    title: "Last Updated",
    dataIndex: "lastUpdatedAt",
    key: "lastUpdatedAt",
    width: 140,
    render: (value: string) => (value ? dayjs(value).format("M/D h:mm a") : ""),
    sorter: (a, b) =>
      String(a.lastUpdatedAt ?? "").localeCompare(
        String(b.lastUpdatedAt ?? "")
      ),
  },
]
